class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    
    // Initialize drag scroll if recommendations are already loaded
    if (this.dataset.recommendationsPerformed === 'true') {
      this.#initializeDragScroll();
    }
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    this.#activeFetch?.abort();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          this.#initializeDragScroll();
          this.#initializeEmblaCarousel();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }





  /**
   * Initialize drag-to-scroll functionality for desktop users
   */
  #initializeDragScroll() {
    const wrapper = this.querySelector('.product-recommendations__wrapper');
    if (!wrapper) return;

    // Only enable drag on devices with fine pointers (desktop/laptop)
    if (!window.matchMedia('(pointer: fine)').matches) return;

    let isDown = false;
    let startX;
    let scrollLeft;

    const checkScrollable = () => {
      return wrapper.scrollWidth > wrapper.clientWidth;
    };

    const updateCursor = () => {
      wrapper.style.cursor = checkScrollable() ? 'grab' : '';
    };

    const handleMouseDown = (e) => {
      if (!checkScrollable()) return;

      isDown = true;
      wrapper.style.cursor = 'grabbing';
      wrapper.style.userSelect = 'none';
      wrapper.style.scrollSnapType = 'none';
      startX = e.pageX - wrapper.offsetLeft;
      scrollLeft = wrapper.scrollLeft;
    };

    const handleMouseLeave = () => {
      isDown = false;
      updateCursor();
      wrapper.style.userSelect = '';
      wrapper.style.scrollSnapType = '';
    };

    const handleMouseUp = () => {
      isDown = false;
      updateCursor();
      wrapper.style.userSelect = '';
      wrapper.style.scrollSnapType = '';
    };

    const handleMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - wrapper.offsetLeft;
      const walk = (x - startX) * 0.8;
      wrapper.scrollLeft = scrollLeft - walk;
    };

    // Set initial cursor and add event listeners
    updateCursor();
    wrapper.addEventListener('mousedown', handleMouseDown);
    wrapper.addEventListener('mouseleave', handleMouseLeave);
    wrapper.addEventListener('mouseup', handleMouseUp);
    wrapper.addEventListener('mousemove', handleMouseMove);
  }

  /**
   * Initialize Embla Carousel for the recommendations
   */
  #initializeEmblaCarousel() {
    // Find the carousel element that was created outside the product-recommendations element
    const sectionId = this.dataset.sectionId;
    const carouselNode = document.querySelector(`#rtnu-recs-${sectionId}`);

    if (!carouselNode) return;

    // Wait for Embla to be available if it's not loaded yet
    if (typeof EmblaCarousel === 'undefined') {
      // Retry after a short delay
      setTimeout(() => this.#initializeEmblaCarousel(), 100);
      return;
    }

    // Check if already initialized
    if (carouselNode._emblaInitialized) return;
    carouselNode._emblaInitialized = true;

    const viewport = carouselNode.querySelector('.embla__viewport');
    const dotsContainer = carouselNode.querySelector('.embla__dots');

    if (!viewport) return;

    const options = {
      align: 'start',
      containScroll: 'trimSnaps',
      slidesToScroll: 1,
    };

    // Initialize Embla
    const embla = EmblaCarousel(viewport, options);

    // Create pagination dots function
    const createDots = () => {
      if (!dotsContainer) return;

      const scrollSnaps = embla.scrollSnapList();
      const snapCount = scrollSnaps.length;
      dotsContainer.innerHTML = '';

      // Only show dots if there are multiple slides
      if (snapCount <= 1) {
        dotsContainer.style.display = 'none';
        return;
      }

      dotsContainer.style.display = 'flex';

      for (let i = 0; i < snapCount; i++) {
        const dot = document.createElement('button');
        dot.className = 'embla__dot';
        dot.type = 'button';
        dot.setAttribute('aria-label', `Go to item ${i + 1}`);
        dot.addEventListener('click', () => embla.scrollTo(i));
        dotsContainer.appendChild(dot);
      }
    };

    // Update dots function
    const updateDots = () => {
      if (!dotsContainer) return;

      const dots = dotsContainer.querySelectorAll('.embla__dot');
      const selectedIndex = embla.selectedScrollSnap();

      dots.forEach((dot, index) => {
        dot.classList.toggle('is-selected', index === selectedIndex);
      });
    };

    // Event listeners
    embla.on('select', updateDots);
    embla.on('init', () => {
      createDots();
      updateDots();
    });

    // Handle resize with debounce
    let resizeTimer;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        embla.reInit();
        createDots();
        updateDots();
      }, 150);
    };

    window.addEventListener('resize', handleResize);

    // Store cleanup function
    carouselNode._emblaCleanup = () => {
      window.removeEventListener('resize', handleResize);
      if (embla) embla.destroy();
    };

    // Store embla API for potential external access
    carouselNode._emblaApi = embla;
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
